import { shift_days } from "@utils/datetime";
import { captureException } from "@utils/error";
import { NoomError } from "@utils/error/NoomError";

import { Plan, updatePlans } from "src/utils/redux/slices/plans";
import { trackBuyflowEvent, trackEvent } from "src/utils/api/tracker";
import { queryClient, send } from "src/utils/fetch";
import { SelectedMedPlanType } from "src/utils/medPlanTypes";
import {
  BRANDED_ASYNC_PRODUCT_NAME,
  BRANDED_SYNC_PRODUCT_NAME,
} from "src/utils/medProductNames";

import { ServerContextState } from "./redux/slices/serverContext";
import getStore from "./redux/store";
import {
  isAffiliateWithTrial,
  isAppIosWinback,
  isCalorieClinicalRoute,
  isClinicalRoute,
  isCore,
  isHealthRoute,
  isInApp,
  isIntl,
  isUpsellRoute,
  isUS,
  isWinback,
} from "./userSegment";
import { formatPriceWithCurrencySymbol } from "./priceUtils";
import saveToBraze, { addDataToCRMQueue } from "./brazeUploader";
import {
  RecommendedPlanState,
  updateRecommendedPlan,
} from "./redux/slices/recommendedPlan";
import mapPlanKeyToNoomPlanId, {
  clinicalPlanIdPrefix,
  clinicalPlans,
  hrtPlanIdPrefix,
  hrtPlans,
  medTierPlanPrefix,
} from "./plansMapping";
import { prepareGrowthAPIParameters } from "./services/api-params";
import {
  getSelectedMedPlanType,
  hasMedicareInsurance,
  isAffiliateTrial30PercentOff,
  isEligibleForStandaloneHRTBuyflow,
  isInAppNativePurchaseEligible,
  isMicrodoseMonthlyEnabled,
  isMicrodoseNoTrialEnabled,
  isMicrodoseOneVsThreeEnabled,
  linkoutFeatureFlagIsEnabled,
  shouldRecommendMDS1MonthAsDefault,
  shouldUseBrandedAsyncPlan,
} from "./userSegment/features";
import { getCountryCode, getMeristemContext } from "./meristemContext";
import { getSessionState } from "src/pageDefinitions/session";
import { MED_SKU } from "src/utils/medSku";
import {
  getEligibleMedProducts,
  HealthPlan,
  MED_GLP1_WEIGHT_LOSS_PLAN_SKUS,
  NON_MED_PLAN,
} from "./medEligibility";
import { TierTypeEnumProto_TierType } from "@noom/noom-contracts/noom_contracts/events/buyflow/buyflow";
import { DEFAULT_NW_PLAN_DURATION_MED } from "./constants";
import { BillingIntervalProto_Unit } from "@noom/noom-contracts/noom_contracts/billing/billing_interval";
import {
  getHRTRecommendedPlanTier,
  getMedRecommendedPlanTier,
} from "@components/med/planInformation/utils";
import { getURLParams } from "./urlParams";
import { trackTask } from "./monitoring/events/tasks";
import { getLoadedSubscriptionStatus } from "src/hooks/subscriptionStatus";
import { LoadSaleItemIdsForNoomPlanIdResponseProto } from "@noom/noom-contracts/noom_contracts/growth/product_catalog/growth_product_catalog_service";

/**
 * Mapping of semantic property names to their Iterable key names.
 */
export const ITERABLE_RECOMMENDED_PLAN_PROPERTIES = {
  recommendedPlanUrl: "recommended_plan_url",
  recommendedPlanUrl7Day: "recommended_plan_url_7day",
  recommendedPlanUrlMed: "recommended_plan_url_med",
  recommendedPlanPrice: "recommended_plan_price",
  recommendedPlanMonthlyPrice: "recommended_plan_monthly_price",
  recommendedPlanRegularPrice: "recommended_plan_regular_price",
  recommendedPlanRegularMonthlyPrice: "recommended_plan_regular_monthly_price",
  recommendedPlanDuration: "recommended_plan_duration",
  recommendedPlanStarterFee: "recommended_plan_starter_fee",
  recommendedPlanType: "recommended_plan_type",
  recommendedMonthGoal: "recommended_month_goal",
  targetBmi: "target_bmi",
  forcedNoomPlanId: "forcedNoomPlanId",
} as const;

export const HM_CURRICULUM = "HM";
export const VIP_HM_CURRICULUM = "VIP_HM";
export const NATIVE_IOS_PLAN_KEY_SUFFIX = "-IN-APP-IOS";
export const NATIVE_IOS_14_TRIAL_PLAN_KEY_SUFFIX = "-IN-APP-IOS-14-TRIAL";
export const NATIVE_IOS_LINKOUT_PLAN_KEY_SUFFIX = "-IN-APP-IOS-LINKOUT";
export const NATIVE_ANDROID_PLAN_KEY_SUFFIX = "-IN-APP-ANDROID";

export const NATIVE_PLAN_KEY_SUFFIXES = [
  NATIVE_IOS_PLAN_KEY_SUFFIX,
  NATIVE_IOS_14_TRIAL_PLAN_KEY_SUFFIX,
  NATIVE_IOS_LINKOUT_PLAN_KEY_SUFFIX,
  NATIVE_ANDROID_PLAN_KEY_SUFFIX,
] as const;

// c/p logic from growth repo
const GEO_DATA_TO_CURRENCY = {
  // By countries.
  US: "USD",
  CA: "CAD",
  GB: "GBP",
  AU: "AUD",
  NZ: "NZD",
  JP: "JPY",
  KR: "KRW",
  // By continents.
  EU: "EUR",
  NA: "USD",
};

export const getUserCurrency = () => {
  const countryCode = getCountryCode();
  const { continent_code } = getMeristemContext();

  if (GEO_DATA_TO_CURRENCY[countryCode]) {
    return GEO_DATA_TO_CURRENCY[countryCode];
  }
  if (GEO_DATA_TO_CURRENCY[continent_code]) {
    return GEO_DATA_TO_CURRENCY[continent_code];
  }
  return GEO_DATA_TO_CURRENCY.US;
};

export const getNoomPlanIdFromKey = (key: string) => {
  const noomPlanId = mapPlanKeyToNoomPlanId[key];

  if (!noomPlanId) {
    // should not happen, we mapped all keys defined in growth
    trackEvent("CannotMapKeyToNoomPlanId", { key });
    throw new Error(`Missing plan key mapping for: ${key}`);
  }

  return noomPlanId;
};

export const getNoomPlanKeyFromId = (id: string | undefined) => {
  return Object.keys(mapPlanKeyToNoomPlanId).find(
    (key) => mapPlanKeyToNoomPlanId[key] === id
  );
};

function getCompoundedPlanKey(): string {
  if (isCalorieClinicalRoute()) return clinicalPlans.compoundedCalorie;
  return clinicalPlans.compounded;
}

function getBrandedPlanKey(): string {
  if (shouldUseBrandedAsyncPlan()) {
    return clinicalPlans.brandedAsync;
  }
  return clinicalPlans.branded;
}

function getCompoundedMicrodosePlanKey(): string {
  if (isCalorieClinicalRoute()) return clinicalPlans.compoundedMicrodoseCalorie;
  if (isMicrodoseNoTrialEnabled()) {
    return clinicalPlans.compoundedMicrodoseNoTrial;
  }
  if (isMicrodoseOneVsThreeEnabled() && shouldRecommendMDS1MonthAsDefault()) {
    return clinicalPlans.compoundedMicrodoseOneMonth;
  }
  return getBaselineMicrodosePlanKey();
}

/**
 * This function will return the baseline mds plan without accounting for routes and other params
 */
export function getBaselineMicrodosePlanKey(): string {
  if (isMicrodoseMonthlyEnabled()) {
    return clinicalPlans.compoundedMicrodoseOneMonth;
  }
  return clinicalPlans.compoundedMicrodose;
}

export function getMedPlanKeyBySKU(
  medSKU: MED_SKU
): keyof typeof mapPlanKeyToNoomPlanId {
  const mapping: Record<MED_SKU, string> = {
    [MED_SKU.BRANDED]: getBrandedPlanKey(),
    [MED_SKU.BRANDED_ZEPBOUND_VIAL]: getBrandedPlanKey(),
    [MED_SKU.BRANDED_WEGOVY_PILL]: getBrandedPlanKey(),
    [MED_SKU.COMPOUNDED]: getCompoundedPlanKey(),
    [MED_SKU.COMPOUNDED_TIRZEPATIDE]: clinicalPlans.compoundedTirzepatide,
    [MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE]:
      clinicalPlans.compoundedMicrodoseTirzepatide,
    [MED_SKU.COMPOUNDED_MICRODOSE]: getCompoundedMicrodosePlanKey(),
    [MED_SKU.GENERIC_LIRAGLUTIDE]: clinicalPlans.genericLiraglutide,
    [MED_SKU.ORALS_METFORMIN]: clinicalPlans.oralsMetformin,
    [MED_SKU.HRT_CREAM]: hrtPlans.cream,
    [MED_SKU.HRT_PATCH]: hrtPlans.patch,
    [MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER]:
      clinicalPlans.compoundedMicrodoseProactiveHealthBiomarker,
  };
  return mapping[medSKU];
}

/**
 * MED_SKU → catalog product name(s). Product names are the universal
 * identifiers shared with billing-core / offers. `null` means the SKU has no
 * catalog product of its own (BRANDED_ZEPBOUND_VIAL / BRANDED_WEGOVY_PILL are
 * sold through the BRANDED fulfillment-variant products). Branded product
 * names come from the leaf module `medProductNames.ts`.
 */
export const MED_SKU_TO_PRODUCT_NAME: Record<
  MED_SKU,
  string | string[] | null
> = {
  [MED_SKU.BRANDED]: [BRANDED_SYNC_PRODUCT_NAME, BRANDED_ASYNC_PRODUCT_NAME],
  [MED_SKU.COMPOUNDED]: "telehealth-medtier-compounded",
  [MED_SKU.COMPOUNDED_TIRZEPATIDE]: "telehealth-medtier-compounded-tirzepatide",
  [MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE]:
    "telehealth-medtier-compounded-microdose-tirzepatide",
  [MED_SKU.BRANDED_ZEPBOUND_VIAL]: null,
  [MED_SKU.BRANDED_WEGOVY_PILL]: null,
  [MED_SKU.COMPOUNDED_MICRODOSE]: "telehealth-medtier-compounded-microdose",
  [MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER]:
    "telehealth-medtier-compounded-microdose-biomarker-longevity",
  [MED_SKU.ORALS_METFORMIN]: "telehealth-medtier-orals-metformin",
  [MED_SKU.GENERIC_LIRAGLUTIDE]: "telehealth-medtier-generic-liraglutide",
  [MED_SKU.HRT_CREAM]: "telehealth-medtier-mht-cream",
  [MED_SKU.HRT_PATCH]: "telehealth-medtier-mht-patch",
};

/**
 * Maps a product name (like "telehealth-medtier-compounded") to a MED_SKU enum value.
 * Returns null if the product name doesn't match any known MED_SKU.
 */
export function getMedSKUByProductName(productName: string): MED_SKU | null {
  const entry = (
    Object.entries(MED_SKU_TO_PRODUCT_NAME) as [
      MED_SKU,
      string | string[] | null
    ][]
  ).find(([, value]) =>
    Array.isArray(value) ? value.includes(productName) : value === productName
  );

  return entry ? entry[0] : null;
}

export const getMedPlanIdBySKU = (sku: MED_SKU) => {
  return mapPlanKeyToNoomPlanId[getMedPlanKeyBySKU(sku)];
};

export const isClinicalPlanKey = (planKey: string | undefined) => {
  const clinicalPlanKeys = Object.values(clinicalPlans);
  return clinicalPlanKeys.includes(planKey as any);
};

export const isClinicalTierNoomPlanId = (noomPlanId: string | undefined) => {
  return isClinicalPlanKey(getNoomPlanKeyFromId(noomPlanId));
};

export const isBrandedNoomPlanId = (noomPlanId: string | undefined) => {
  return noomPlanId === getMedPlanIdBySKU(MED_SKU.BRANDED);
};

export const isBrandedAsyncNoomPlanId = (noomPlanId: string | undefined) => {
  return noomPlanId === mapPlanKeyToNoomPlanId[clinicalPlans.brandedAsync];
};

export const isGenericLiraglutideNoomPlanId = (
  noomPlanId: string | undefined
) => {
  return noomPlanId === getMedPlanIdBySKU(MED_SKU.GENERIC_LIRAGLUTIDE);
};

export const isCompoundedNoomPlanId = (noomPlanId: string | undefined) => {
  return noomPlanId === getMedPlanIdBySKU(MED_SKU.COMPOUNDED);
};

export const isCompoundedTirzepatideNoomPlanId = (
  noomPlanId: string | undefined
) => {
  return noomPlanId === getMedPlanIdBySKU(MED_SKU.COMPOUNDED_TIRZEPATIDE);
};

export const isCompoundedMicrodoseTirzepatideNoomPlanId = (
  noomPlanId: string | undefined
) => {
  return (
    noomPlanId === getMedPlanIdBySKU(MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE)
  );
};

export const isCompoundedMicrodoseNoomPlanId = (
  noomPlanId: string | undefined
) => {
  return noomPlanId === getMedPlanIdBySKU(MED_SKU.COMPOUNDED_MICRODOSE);
};

export const isOralsMetforminNoomPlanId = (noomPlanId: string | undefined) => {
  return noomPlanId === getMedPlanIdBySKU(MED_SKU.ORALS_METFORMIN);
};

export const isHRTPlanKey = (planKey: string | undefined) => {
  return Object.values(hrtPlans).includes(planKey);
};

export const isHRTNoomPlanId = (noomPlanId: string | undefined) => {
  return isHRTPlanKey(getNoomPlanKeyFromId(noomPlanId));
};

export const isMedTierNoomPlanId = (noomPlanId: string | undefined) => {
  return isClinicalTierNoomPlanId(noomPlanId) || isHRTNoomPlanId(noomPlanId);
};

export const isHRTCreamNoomPlanId = (noomPlanId: string | undefined) => {
  return noomPlanId === getMedPlanIdBySKU(MED_SKU.HRT_CREAM);
};

export const isHRTPatchNoomPlanId = (noomPlanId: string | undefined) => {
  return noomPlanId === getMedPlanIdBySKU(MED_SKU.HRT_PATCH);
};

export const isCompoundedMicrodoseProactiveHealthBiomarkerNoomPlanId = (
  noomPlanId: string | undefined
) => {
  return (
    noomPlanId ===
    getMedPlanIdBySKU(MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER)
  );
};

/**
 * Determines the plan length using the plan id
 * The plans mapping keys contain the plan duration. The key has the following structure:
 * <plan_duration><separator><extra_plan_details>
 * <separator> can be "-" or "_"
 */
export const getNoomPlanDurationFromID = (
  noom_plan_id: string
): number | null => {
  const plan: [string, string] = Object.entries(mapPlanKeyToNoomPlanId).find(
    ([, plan_id]) => plan_id === noom_plan_id
  );

  if (!plan) {
    // should not happen, we mapped all keys defined in growth
    captureException(
      new NoomError("CannotMapNoomPlanIdToKey", { context: { noom_plan_id } })
    );
    return null;
  }
  const planDuration = plan[0] || "";
  const separator = planDuration.includes("-") ? "-" : "_";
  return +planDuration.split(separator)[0] || null;
};

export function getDefaultTrialFee(plan: RecommendedPlanState) {
  if (plan?.trial_item_options?.length > 0) {
    const prices = plan.trial_item_options.map((o) => o.price);
    const rawFee = Math.min(...prices);
    return rawFee < 1 && rawFee > 0 ? +rawFee.toFixed(2) : rawFee;
  }
  return 0;
}

/**
 * Determine if user eligible for 7 day trial.
 */
export function has7DayTrial() {
  return (
    isUS() &&
    (isCore() ||
      isInApp() ||
      isWinback() ||
      isClinicalRoute() ||
      isHealthRoute() ||
      isUpsellRoute())
  );
}

export function isEligibleFor30DayTrial() {
  const searchParams = new URLSearchParams(window.location.search);
  const trial_length = searchParams.get("trial_length");
  return (
    (isUS() && isAffiliateWithTrial() && trial_length === "30") ||
    isAffiliateTrial30PercentOff() ||
    hasMedicareInsurance()
  );
}

export function isEligibleFor21DayTrial() {
  const { trial_length } = getURLParams();
  return isUS() && isAffiliateWithTrial() && trial_length === "21";
}

/**
 * Determine the length of user's trial for in trial users
 */
export function getTrialLength(serverContext: ServerContextState) {
  const subscriptions = JSON.parse(serverContext.subscriptions);
  const subscription = subscriptions.find((sub) => sub.status === "ACTIVE");
  if (subscription) {
    if (/32_day/.test(subscription.plan_id)) {
      return 32;
    }
    if (/22_day/.test(subscription.plan_id)) {
      return 22;
    }
    if (/8_day/.test(subscription.plan_id)) {
      return 8;
    }
  }
  return 15;
}

export function getMedTrialFeeInUSD(sku: MED_SKU | "NW") {
  if (sku === "NW") {
    return 0.5;
  }

  const planId = getMedPlanIdBySKU(sku);

  if (isCompoundedTirzepatideNoomPlanId(planId)) {
    return 149;
  }

  if (isCompoundedMicrodoseTirzepatideNoomPlanId(planId)) {
    return 99;
  }

  if (isBrandedNoomPlanId(planId)) {
    return 39;
  }
  if (
    isCompoundedNoomPlanId(planId) ||
    isGenericLiraglutideNoomPlanId(planId)
  ) {
    return 129;
  }

  if (isCompoundedMicrodoseNoomPlanId(planId)) {
    return 79;
  }
  if (isCompoundedMicrodoseProactiveHealthBiomarkerNoomPlanId(planId)) {
    return 149;
  }
  if (isOralsMetforminNoomPlanId(planId)) {
    return 69;
  }
  if (isHRTCreamNoomPlanId(planId)) {
    return 69;
  }
  if (isHRTPatchNoomPlanId(planId)) {
    return 99;
  }
  return 0.5;
}

/**
 * Determine whether a plan is part of the HM curriculum
 */
export function isHMPlan(plan: Partial<Plan>) {
  return [HM_CURRICULUM, VIP_HM_CURRICULUM].includes(plan.curriculum);
}

export async function fetchProductPlanAsPlanById(noomPlanId: string) {
  const { dispatch, getState } = getStore();
  const state = getState();
  const currency = getUserCurrency();
  const planKey = `${noomPlanId}_${currency}`;

  const plan = state.plans[planKey];
  if (plan) {
    return plan;
  }

  try {
    const url = `/services/api/product_catalog/v1/productAsPlan/${encodeURIComponent(
      currency
    )}/${encodeURIComponent(noomPlanId)}`;

    const result = await send<Plan>("GET", url, null, {
      params: prepareGrowthAPIParameters(),
    });
    result.noom_plan_id = noomPlanId;

    dispatch(
      updatePlans({
        [planKey]: result,
      })
    );

    return result;
  } catch (e) {
    captureException(e);
    return null;
  }
}

export async function fetchProductPlanForExistingUser(
  noomPlanId: string,
  promoCode?: string,
  use45PercentDiscount = false
) {
  try {
    const url = `/api/plans/getPlanForExistingUser/${encodeURIComponent(
      noomPlanId
    )}`;

    const params = new URLSearchParams();
    if (promoCode) {
      params.set("promoCode", promoCode);
    }
    if (use45PercentDiscount) {
      params.set("useCounterOffer45Discount", "true");
    }

    const result = await send<
      Plan & {
        sales_tax_rate: number;
        is_tax_inclusive: boolean;
      }
    >("GET", url, null, {
      params: prepareGrowthAPIParameters(params),
    });
    result.noom_plan_id = noomPlanId;

    return result;
  } catch (e) {
    captureException(e);
    return null;
  }
}

async function fetchNativeAppPlan(key: string) {
  const { dispatch } = getStore();
  const currency = getUserCurrency();
  try {
    // backend returns a regular plan object but with one added property of itunes_product_id OR android_product_id
    const url = `/services/api/product_catalog/v1/nativeAppProductAsPlan/${encodeURIComponent(
      currency
    )}/${encodeURIComponent(key)}`;

    const result = await send<Plan>("GET", url, null, {
      params: prepareGrowthAPIParameters(),
    });
    result.noom_plan_id = key;

    dispatch(
      updatePlans({
        [`${key}_${currency}`]: result,
        [key]: result,
      })
    );

    dispatch(updateRecommendedPlan(result));

    return result;
  } catch (e) {
    captureException(e);
    return null;
  }
}

export async function loadSalesItemIds(
  noomPlanId: string,
  currency: string
): Promise<LoadSaleItemIdsForNoomPlanIdResponseProto> {
  const task = trackTask("LoadSalesItemIds", {
    noomPlanId,
    currency,
  });
  task.start();
  try {
    const response = await queryClient.fetchQuery({
      queryKey: ["loadSalesItemIds", noomPlanId, currency],
      queryFn: () =>
        send<LoadSaleItemIdsForNoomPlanIdResponseProto>(
          "GET",
          `/services/api/product_catalog/v1/loadSaleItemIdsForNoomPlanId/?currency=${currency}&noomPlanId=${noomPlanId}`,
          undefined
        ),
    });

    task.success();
    return response;
  } catch (ex) {
    task.failed(ex);
    throw ex;
  }
}

export function getNativeAppPlanKey(planDuration: number): string {
  if (linkoutFeatureFlagIsEnabled() && isUS()) {
    return `${planDuration}${NATIVE_IOS_LINKOUT_PLAN_KEY_SUFFIX}`;
  }
  if (isIntl() && isAppIosWinback()) {
    return `${planDuration}${NATIVE_IOS_14_TRIAL_PLAN_KEY_SUFFIX}`;
  }
  return `${planDuration}${NATIVE_IOS_PLAN_KEY_SUFFIX}`;
}

export function isNativeAppPlanKey(noomPlanId: string | undefined): boolean {
  if (!noomPlanId) {
    return false;
  }
  return NATIVE_PLAN_KEY_SUFFIXES.some((suffix) => noomPlanId.endsWith(suffix));
}

const keysToCheckForDiscrepancies = ["braintree_id", "price"];

export const getPlanByKey = async (key: string | number): Promise<Plan> => {
  // native app plans (Android, iOS) go through a different growth API call
  if (isNativeAppPlanKey(`${key}`)) {
    return fetchNativeAppPlan(`${key}`);
  }

  const oldPlan = getStore().getState()?.plans?.[key];
  const noomPlanId = getNoomPlanIdFromKey(`${key}`);
  const productPlanAsPlan = await fetchProductPlanAsPlanById(noomPlanId);

  // compare old plan and fetched plan
  const discrepancies = [];
  let status = "match";

  if (!productPlanAsPlan) {
    status = "notFound";
  } else if (oldPlan) {
    // use keys of an old plan, since transformed product plan will contain
    // additional properties (e.g. )
    // only run the comparison in landing context (payment context has no plans dict)
    Object.keys(oldPlan).forEach((k) => {
      if (
        keysToCheckForDiscrepancies.includes(k) &&
        oldPlan[k] !== productPlanAsPlan[k]
      ) {
        discrepancies.push(k);
        status = "discrepancy";
      }
    });
  }

  trackEvent("ProducPlanAsPlanSearch", {
    key,
    noomPlanId,
    oldPlan,
    productPlanAsPlan,
    status,
    discrepancies,
  });

  return productPlanAsPlan ?? oldPlan;
};

export async function savePlanToBraze(
  data: {
    recommendedPlan: RecommendedPlanState;
    currentBMI: number;
    targetBMI?: number;
    targetMonthNameFull?: string;
  },
  shouldSkipWeightLossData = false,
  useQueue = false
) {
  let recommendedNWEmailPlan: RecommendedPlanState | null = null;
  let recommendedPlan7DaysNoomPlanId;
  let recommendedPlanType: HealthPlan = NON_MED_PLAN.NOOM_WEIGHT;

  const recommendedMedPlanId = isMedTierNoomPlanId(
    data.recommendedPlan?.noom_plan_id
  )
    ? data.recommendedPlan?.noom_plan_id
    : undefined;

  if (recommendedMedPlanId) {
    recommendedPlanType = getMedSKUByPlanID(recommendedMedPlanId);
  }

  if (!shouldSkipWeightLossData) {
    if (recommendedMedPlanId) {
      // Note(Alex): Med users also get the regular NW emails so we need to fill the existing fields
      // with data related to a regular NW plan. For med related emails we are sending a standalone field.
      recommendedNWEmailPlan = await getPlanByKey(
        `${DEFAULT_NW_PLAN_DURATION_MED}`
      );
      recommendedPlan7DaysNoomPlanId = isUS()
        ? getNoomPlanIdFromKey(`${DEFAULT_NW_PLAN_DURATION_MED}-7-DAY-TRIAL`)
        : "";
    } else {
      switch (data.recommendedPlan.trial_duration) {
        case 0:
        case 14:
          recommendedNWEmailPlan = data.recommendedPlan;
          break;
        default:
          recommendedNWEmailPlan = await getPlanByKey(
            `${data.recommendedPlan.billing_cycle_duration}`
          );
      }

      // Native app plans cannot be purchased on web, reassign to web-eligible plans
      if (isInAppNativePurchaseEligible()) {
        recommendedNWEmailPlan = await getPlanByKey(
          `${data.recommendedPlan.billing_cycle_duration}`
        );
      }
      recommendedPlan7DaysNoomPlanId = isUS()
        ? getNoomPlanIdFromKey(
            `${data.recommendedPlan.billing_cycle_duration}-7-DAY-TRIAL`
          )
        : "";
    }
  }

  const recommendedProperties = ITERABLE_RECOMMENDED_PLAN_PROPERTIES;

  const getNWPlanProperties = (plan: RecommendedPlanState | null) => {
    if (!plan) {
      return {
        [recommendedProperties.recommendedPlanUrl]: null,
        [recommendedProperties.recommendedPlanRegularPrice]: null,
        [recommendedProperties.recommendedPlanRegularMonthlyPrice]: null,
        [recommendedProperties.recommendedPlanPrice]: null,
        [recommendedProperties.recommendedPlanMonthlyPrice]: null,
        [recommendedProperties.recommendedPlanDuration]: null,
        [recommendedProperties.recommendedPlanUrl7Day]: null,
      };
    }
    const formatPrice = (price: number) =>
      formatPriceWithCurrencySymbol(price, plan);
    return {
      [recommendedProperties.recommendedPlanUrl]: plan.noom_plan_id,
      [recommendedProperties.recommendedPlanRegularPrice]: formatPrice(
        plan.regular_price
      ),
      [recommendedProperties.recommendedPlanRegularMonthlyPrice]: formatPrice(
        plan.regular_monthly_price
      ),
      [recommendedProperties.recommendedPlanPrice]: formatPrice(plan.price),
      [recommendedProperties.recommendedPlanMonthlyPrice]: formatPrice(
        plan.monthly_price
      ),
      [recommendedProperties.recommendedPlanDuration]:
        plan.billing_cycle_duration,
      ...(plan.isForcedPlan && {
        [recommendedProperties.forcedNoomPlanId]: plan.noom_plan_id,
      }),
      ...(plan.starter_fee && {
        [recommendedProperties.recommendedPlanStarterFee]: formatPrice(
          plan.starter_fee
        ),
      }),
    };
  };

  const properties: JsonObject = {
    current_bmi: data.currentBMI?.toFixed(1),
    ...getNWPlanProperties(recommendedNWEmailPlan),
    ...(recommendedPlan7DaysNoomPlanId && {
      [recommendedProperties.recommendedPlanUrl7Day]:
        recommendedPlan7DaysNoomPlanId,
    }),
    ...(recommendedMedPlanId && {
      [recommendedProperties.recommendedPlanUrlMed]: recommendedMedPlanId,
    }),
    ...(recommendedPlanType && {
      [recommendedProperties.recommendedPlanType]: recommendedPlanType,
    }),
    ...(data.targetBMI != null && {
      [recommendedProperties.targetBmi]: data.targetBMI.toFixed(1),
    }),
    ...(data.targetMonthNameFull && {
      [recommendedProperties.recommendedMonthGoal]: data.targetMonthNameFull,
    }),
  };

  if (isClinicalRoute() || isHealthRoute()) {
    const eventProperties = {
      eligibleMedPlans: getEligibleMedProducts(),
      recommendedPlanType,
      recommendedPlanUrl: recommendedNWEmailPlan?.noom_plan_id ?? undefined,
      recommendedPlanUrlMed: recommendedMedPlanId ?? undefined,
    };

    trackBuyflowEvent("CrmRecommendedPlanDetermined", eventProperties);
  }

  if (useQueue) {
    addDataToCRMQueue({ user: properties });
  } else {
    await saveToBraze({ user: properties });
  }
}

export const calculateFirstChargeDate = (trialDuration: number) => {
  return shift_days(trialDuration + 1, new Date());
};

export function isPremiumTierPlanId(planId?: string) {
  return planId?.startsWith("healthy_weight_premium");
}

/** Med catalog product names use the `telehealth-medtier-` prefix. */
export function isMedProductName(productName?: string | null) {
  return !!productName?.startsWith("telehealth-medtier-");
}

/** Active med plan is a GLP-1 weight-loss SKU (excludes orals such as metformin). */
export function isGlp1WeightLossMedProductName(productName?: string | null) {
  if (!productName) {
    return false;
  }

  const medSKU = getMedSKUByProductName(productName);
  return !!medSKU && MED_GLP1_WEIGHT_LOSS_PLAN_SKUS.includes(medSKU);
}

/** Weight and Premium catalog product names include `healthy-weight`. */
export function isWeightOrPremiumProductName(productName?: string | null) {
  return !!productName?.includes("healthy-weight");
}

// TODO: refactor to use billing API https://noomhq.atlassian.net/browse/GPA-723
export function isMedTierPlanId(planId: string) {
  if (!planId) return false;

  return Boolean(getCurrentMedTierSKU(planId));
}

// TODO: refactor to use billing API https://noomhq.atlassian.net/browse/GPA-723
export function isCompoundedMedTier(planId: string) {
  return planId?.startsWith(clinicalPlanIdPrefix.compounded);
}

export function isCompoundedTirzepatideMedTier(planId: string) {
  return planId?.startsWith(clinicalPlanIdPrefix.compoundedTirzepatide);
}

export function isCompoundedMicrodoseTirzepatideMedTier(planId: string) {
  return planId?.startsWith(
    clinicalPlanIdPrefix.compoundedMicrodoseTirzepatide
  );
}

export function isGenericLiraglutideMedTier(planId: string) {
  return planId?.startsWith(clinicalPlanIdPrefix.genericLiraglutide);
}

export function isBrandedMedTier(planId: string) {
  return (
    planId?.startsWith(clinicalPlanIdPrefix.branded) ||
    planId?.startsWith(clinicalPlanIdPrefix.brandedAsync)
  );
}

export function isOralsMetforminMedTier(planId: string) {
  return planId?.startsWith(clinicalPlanIdPrefix.oralsMetformin);
}

export function isCompoundedMicrodoseMedTier(planId: string) {
  return planId?.startsWith(clinicalPlanIdPrefix.compoundedMicrodose);
}

export function isCompoundedMicrodoseProactiveHealthBiomarkerMedTier(
  planId: string
) {
  return planId?.startsWith(
    clinicalPlanIdPrefix.compoundedMicrodoseProactiveHealthBiomarker
  );
}

export function isMedTier(planId: string) {
  return planId?.startsWith(medTierPlanPrefix);
}

export function getSubscriptionType(): "med" | "healthyWeight" | "free" {
  const { serverContext } = getStore().getState();
  const subscriptionStatus = getLoadedSubscriptionStatus();
  const planId = serverContext.current_plan_id || subscriptionStatus?.planId;

  if (!planId) return "free";
  if (isMedTier(planId)) return "med";
  return "healthyWeight";
}

export function isHRTCreamMedTier(planId: string) {
  return planId?.startsWith(hrtPlanIdPrefix.cream);
}

export function isHRTPatchMedTier(planId: string) {
  return planId?.startsWith(hrtPlanIdPrefix.patch);
}

export function isHRTMedTier(planId: string) {
  return isHRTCreamMedTier(planId) || isHRTPatchMedTier(planId);
}

export function getCurrentMedTierSKU(planId: string): MED_SKU | null {
  const mapping: Record<MED_SKU, (planId: string) => boolean> = {
    [MED_SKU.BRANDED]: isBrandedMedTier,
    [MED_SKU.BRANDED_ZEPBOUND_VIAL]: () => false, // We should never map a plan id to the zepbound sku since it should just map to branded instead
    [MED_SKU.BRANDED_WEGOVY_PILL]: () => false, // We should never map a plan id to the wegovy pill sku since it should just map to branded instead
    [MED_SKU.COMPOUNDED]: isCompoundedMedTier,
    [MED_SKU.COMPOUNDED_TIRZEPATIDE]: isCompoundedTirzepatideMedTier,
    [MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE]:
      isCompoundedMicrodoseTirzepatideMedTier,
    [MED_SKU.GENERIC_LIRAGLUTIDE]: isGenericLiraglutideMedTier,
    [MED_SKU.COMPOUNDED_MICRODOSE]: isCompoundedMicrodoseMedTier,
    [MED_SKU.ORALS_METFORMIN]: isOralsMetforminMedTier,
    [MED_SKU.HRT_CREAM]: isHRTCreamMedTier,
    [MED_SKU.HRT_PATCH]: isHRTPatchMedTier,
    [MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER]:
      isCompoundedMicrodoseProactiveHealthBiomarkerMedTier,
  };

  for (const [sku, checker] of Object.entries(mapping)) {
    if (checker(planId)) {
      return sku as MED_SKU;
    }
  }
  return null;
}

export function getTierByPlanId(planId: string): TierTypeEnumProto_TierType {
  if (isMedTierPlanId(planId)) {
    return "MED";
  }
  if (isPremiumTierPlanId(planId)) {
    return "PREMIUM";
  }
  return "MAIN";
}

export function getMedPlanKey(
  eligibleMedProducts: Array<MED_SKU> = getEligibleMedProducts()
): string {
  const isEligibleForStandaloneHRT = isEligibleForStandaloneHRTBuyflow();
  const { selectedMedPlanType, selectedHRTPlanType } =
    getSessionState("browser");

  if (selectedMedPlanType || selectedHRTPlanType) {
    // If the user has explicitly selected a plan from the plan reveal pages, prioritize that
    const selectedPlanType = getSelectedMedPlanType();

    return getMedPlanKeyBySKU(selectedPlanType) as string;
  }

  if (isEligibleForStandaloneHRT) {
    const recommendedSKU = getHRTRecommendedPlanTier();

    return getMedPlanKeyBySKU(recommendedSKU) as string;
  }

  return getMedPlanKeyBySKU(
    getMedRecommendedPlanTier(eligibleMedProducts)
  ) as string;
}

export function getPlanDurationUnitTranslationKey(
  durationUnit: BillingIntervalProto_Unit
) {
  switch (durationUnit) {
    case "DAYS":
      return "day";
    case "WEEKS":
      return "week";
    default:
      return "month";
  }
}

export function getMedSKUByPlanID(planId: string): MED_SKU {
  const mapping: Record<MED_SKU, (planId: string) => boolean> = {
    [MED_SKU.BRANDED]: isBrandedNoomPlanId,
    [MED_SKU.BRANDED_ZEPBOUND_VIAL]: () => false, // We should never map a plan id to the zepbound sku since it should just map to branded instead
    [MED_SKU.BRANDED_WEGOVY_PILL]: () => false, // We should never map a plan id to the wegovy pill sku since it should just map to branded instead
    [MED_SKU.COMPOUNDED]: isCompoundedNoomPlanId,
    [MED_SKU.COMPOUNDED_TIRZEPATIDE]: isCompoundedTirzepatideNoomPlanId,
    [MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE]:
      isCompoundedMicrodoseTirzepatideNoomPlanId,
    [MED_SKU.GENERIC_LIRAGLUTIDE]: isGenericLiraglutideNoomPlanId,
    [MED_SKU.COMPOUNDED_MICRODOSE]: isCompoundedMicrodoseNoomPlanId,
    [MED_SKU.ORALS_METFORMIN]: isOralsMetforminNoomPlanId,
    [MED_SKU.HRT_CREAM]: isHRTCreamNoomPlanId,
    [MED_SKU.HRT_PATCH]: isHRTPatchNoomPlanId,
    [MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER]:
      isCompoundedMicrodoseProactiveHealthBiomarkerNoomPlanId,
  };

  for (const [sku, checker] of Object.entries(mapping)) {
    if (checker(planId)) {
      return sku as MED_SKU;
    }
  }
  return null;
}

export function getSelectedMedPlanTypeByPlanId(
  planId: string
): SelectedMedPlanType | undefined {
  const mapping: Record<MED_SKU, SelectedMedPlanType | undefined> = {
    [MED_SKU.BRANDED]: "branded",
    [MED_SKU.BRANDED_ZEPBOUND_VIAL]: "branded",
    [MED_SKU.BRANDED_WEGOVY_PILL]: "branded",
    [MED_SKU.COMPOUNDED]: "compounded",
    [MED_SKU.COMPOUNDED_TIRZEPATIDE]: "compoundedTirzepatide",
    [MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE]:
      "compoundedMicrodoseTirzepatide",
    [MED_SKU.GENERIC_LIRAGLUTIDE]: "genericLiraglutide",
    [MED_SKU.COMPOUNDED_MICRODOSE]: "compoundedMicrodose",
    [MED_SKU.ORALS_METFORMIN]: "oralsMetformin",
    [MED_SKU.HRT_CREAM]: undefined,
    [MED_SKU.HRT_PATCH]: undefined,
    [MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER]:
      "compoundedMicrodoseProactiveHealthBiomarker",
  };

  const sku = getMedSKUByPlanID(planId);
  return mapping[sku];
}

/**
 * Get the supply length in weeks for a given medication SKU.
 *
 * Trial supply:
 * - All medication plans have 4-week trial supply.
 *
 * Recurring (subscription) supply:
 * - Compounded, compounded tirzepatide, metformin, compounded microdose, and generic liraglutide plans have 12-week supply.
 * - Compounded microdose one-month plan (Google Shopping traffic with MDS 1-month default) has 4-week supply.
 * - Microdose biomarker plan has 16-week supply.
 * - All other medication plans have 4-week supply.
 */
export function getSupplyLengthInWeeksBySKU(
  medSKU: MED_SKU,
  isTrial?: boolean
): number {
  if (isTrial) {
    return 4;
  }

  if (medSKU === MED_SKU.COMPOUNDED_MICRODOSE) {
    return isMicrodoseOneVsThreeEnabled() && shouldRecommendMDS1MonthAsDefault()
      ? 4
      : 12;
  }
  if (
    medSKU === MED_SKU.COMPOUNDED ||
    medSKU === MED_SKU.COMPOUNDED_TIRZEPATIDE ||
    medSKU === MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE ||
    medSKU === MED_SKU.ORALS_METFORMIN ||
    medSKU === MED_SKU.GENERIC_LIRAGLUTIDE
  ) {
    return 12;
  }
  if (medSKU === MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER) {
    return 16;
  }
  return 4;
}
